/* NOTICE — Spring Stand configurator (sections/notice-spring-configurator.liquid).

   State is two things: how many objects (1-3) and, left to right, the finish of each. Everything on
   the page is drawn from that state:
   - the stage: one element per object, positioned by CSS; changes animate with FLIP (positions and
     sizes are always CSS's, the script only bridges old to new), a finish change cross-fades only
     that object's image, an object leaving fades out as a ghost at its old place;
   - the controls: count options with their prices, one swatch row per object;
   - the price: the plain sum, or the set price while set pricing is live and every chosen finish
     costs the reference unit price (the same rule the Liquid uses for first paint);
   - the bag: each object is taken from the first listing that still has one, after what the cart
     already holds (the Spring Stand's stock is split across listings), so a set never asks the
     cart for more than exists.

   Adding posts the lines to the Ajax cart with the cart-drawer sections, opens the drawer the way
   ADD TO CART does, and publishes PUB_SUB_EVENTS.cartUpdate with source 'product-form' once per
   line (origin 'notice-spring-configurator'), so the theme's listeners run unchanged: Pinterest
   AddToCart, GoKwik's India / international checkout switch, cart.js skipping its own refetch.
   Meta and GA4 AddToCart come from Shopify's own cart events on /cart/add. The pattern follows
   assets/notice-addons.js.

   Nothing is added without a click. */
(function () {
  if (customElements.get('notice-spring-configurator')) return;

  const MAX = 3;
  const MIN_BUSY_MS = 420; // product-form.js uses the same floor so the busy state always paints
  const ADDED_MS = 2600;
  const ERROR_MS = 6000;
  const MOVE_MS = 420;
  const FADE_MS = 260;

  const pubSubEvents = () =>
    (typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS : window.PUB_SUB_EVENTS) || null;
  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  const pad = (n) => String(n).padStart(2, '0');
  const reducedMotion = () =>
    !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  // Cents -> the shop money format, grouping as Liquid's `money` filter renders it, so a currency
  // app converts the result exactly like a server-rendered price (assets/notice-sticky-atc.js).
  function formatMoney(cents, format) {
    const fmt = format || window.shopMoneyFormat || '{{amount}}';
    const delimit = (precision, thousands, decimal) => {
      const n = Number(cents);
      if (!isFinite(n)) return '0';
      const parts = (n / 100).toFixed(precision).split('.');
      const whole = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
      return parts[1] ? whole + decimal + parts[1] : whole;
    };
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
      switch (key) {
        case 'amount_no_decimals':
          return delimit(0, ',', '.');
        case 'amount_with_comma_separator':
          return delimit(2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return delimit(0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return delimit(2, "'", '.');
        case 'amount_no_decimals_with_space_separator':
          return delimit(0, ' ', '.');
        case 'amount_with_space_separator':
          return delimit(2, ' ', ',');
        default:
          return delimit(2, ',', '.');
      }
    });
  }

  const textOf = (html) => {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent.replace(/\s+/g, ' ').trim();
  };

  class NoticeSpringConfigurator extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      const raw = this.querySelector('[data-cfg-data]');
      try {
        this.data = JSON.parse(raw.textContent);
      } catch (error) {
        console.error('notice-spring-configurator: page data unreadable', error);
        return;
      }
      this.ready = true;
      this.finishes = new Map((this.data.finishes || []).map((f) => [f.key, f]));
      if (!this.finishes.size) return;

      this.labels = this.data.labels || {};
      this.inCart = new Map();
      this.timers = new Map();
      this.busy = false;

      this.stage = this.querySelector('[data-cfg-stage]');
      this.list = this.querySelector('[data-cfg-objects]');
      this.objs = [...this.querySelectorAll('[data-cfg-obj]')];
      this.slotEls = [...this.querySelectorAll('[data-cfg-slot]')];
      this.addButton = this.querySelector('[data-cfg-add]');
      this.addLabel = this.querySelector('[data-cfg-add-label]');
      this.errorEl = this.querySelector('[data-cfg-error]');
      this.statusEl = this.querySelector('[data-cfg-status]');
      this.bar = this.querySelector('[data-cfg-bar]');
      this.barAdd = this.querySelector('[data-cfg-bar-add]');

      const keys = [...this.finishes.keys()];
      const slots = (this.data.slots || []).filter((k) => this.finishes.has(k));
      while (slots.length < MAX) slots.push(keys[slots.length % keys.length]);
      this.state = {
        count: Math.min(MAX, Math.max(1, parseInt(this.data.count, 10) || 1)),
        slots: slots.slice(0, MAX),
      };

      this.applyUrlState();
      this.bind();
      this.render({ animate: false });
      this.watchBar();
      this.measureHeader();
      this.refreshCart().then(() => this.render({ animate: false }));
      this.preload();
    }

    disconnectedCallback() {
      if (this.barObserver) this.barObserver.disconnect();
      document.body.classList.remove('nt-cfg-bar-on');
      if (this.unsubscribeCart) this.unsubscribeCart();
      if (this.timers) this.timers.forEach((timer) => clearTimeout(timer));
    }

    /* ---------- events ---------- */

    bind() {
      this.addEventListener('change', (event) => {
        const input = event.target;
        if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;
        if (input.closest('[data-cfg-count]')) {
          this.setCount(parseInt(input.value, 10));
          return;
        }
        const slot = input.closest('[data-cfg-slot]');
        if (slot) this.setFinish(parseInt(slot.dataset.cfgSlot, 10) - 1, input.value);
      });

      this.addEventListener('click', (event) => {
        const move = event.target.closest('[data-cfg-move]');
        if (move && this.contains(move)) {
          const obj = move.closest('[data-cfg-obj]');
          this.move(this.objs.indexOf(obj), parseInt(move.dataset.cfgMove, 10));
          return;
        }
        const add = event.target.closest('[data-cfg-add], [data-cfg-bar-add]');
        if (add && this.contains(add)) this.add(add);
      });

      // Pointing at a swatch previews its name in that row, the way a finish library does; the
      // committed choice comes back when the pointer leaves.
      this.addEventListener('pointerover', (event) => {
        const sw = event.target.closest('[data-cfg-sw]');
        if (sw && this.contains(sw)) {
          const slot = sw.closest('[data-cfg-slot]');
          if (slot) this.renderSlotName(this.slotEls.indexOf(slot), sw.dataset.cfgSw, sw);
        }
        // The stage and the controls point at each other: hovering an object marks its row.
        const obj = event.target.closest('[data-cfg-obj]');
        const index = obj && this.contains(obj) ? this.objs.indexOf(obj) : -1;
        this.slotEls.forEach((el, j) => el.classList.toggle('is-pointed', j === index));
      });
      this.addEventListener('pointerout', (event) => {
        const sw = event.target.closest('[data-cfg-sw]');
        if (!sw || (event.relatedTarget && sw.contains(event.relatedTarget))) return;
        const slot = sw.closest('[data-cfg-slot]');
        if (slot) this.renderSlotName(this.slotEls.indexOf(slot));
      });
      this.addEventListener('pointerleave', () => {
        this.slotEls.forEach((el) => el.classList.remove('is-pointed'));
      });

      const events = pubSubEvents();
      if (events && typeof subscribe === 'function') {
        this.unsubscribeCart = subscribe(events.cartUpdate, (payload) => {
          if (payload && payload.origin === 'notice-spring-configurator') return;
          this.refreshCart().then(() => this.render({ animate: false }));
        });
      }
    }

    /* ---------- state changes ---------- */

    setCount(count) {
      const next = Math.min(MAX, Math.max(1, count || 1));
      if (next === this.state.count) return;
      // A position coming back into view keeps its last finish unless that finish has none left for it.
      for (let i = this.state.count; i < next; i += 1) {
        if (this.remaining(this.state.slots[i], i, next) <= 0) {
          const alt = this.firstAvailable(i, next);
          if (alt) this.state.slots[i] = alt;
        }
      }
      const previous = this.state.count;
      this.state.count = next;
      this.render({ animate: true, previousCount: previous });
    }

    setFinish(index, key) {
      if (!this.finishes.has(key) || this.state.slots[index] === key) return;
      this.state.slots[index] = key;
      this.render({ animate: true, changed: index });
    }

    move(index, direction) {
      const target = index + direction;
      if (index < 0 || target < 0 || target >= this.state.count) return;
      const first = reducedMotion() ? null : this.snapshot();
      [this.state.slots[index], this.state.slots[target]] = [this.state.slots[target], this.state.slots[index]];
      // Swap the elements themselves, so each object travels to its new place instead of changing colour.
      const a = this.objs[index];
      const b = this.objs[target];
      const marker = document.createElement('li');
      this.list.replaceChild(marker, a);
      this.list.replaceChild(a, b);
      this.list.replaceChild(b, marker);
      this.objs[index] = b;
      this.objs[target] = a;
      this.render({ animate: false });
      this.play(first);
      const moved = a.querySelector(`[data-cfg-move="${direction}"]`);
      const fallback = a.querySelector(`[data-cfg-move="${-direction}"]`);
      const focus = moved && !moved.disabled ? moved : fallback;
      if (focus) focus.focus({ preventScroll: true });
    }

    /* ---------- stock ---------- */

    capacity(key) {
      const finish = this.finishes.get(key);
      if (!finish) return 0;
      return finish.sources.reduce(
        (sum, src) => sum + Math.max(0, (src.cap || 0) - (this.inCart.get(src.id) || 0)),
        0
      );
    }

    // How many more objects of this finish the position `index` could take, given the other positions.
    remaining(key, index, count = this.state.count) {
      let used = 0;
      for (let i = 0; i < count; i += 1) if (i !== index && this.state.slots[i] === key) used += 1;
      return this.capacity(key) - used;
    }

    firstAvailable(index, count) {
      return [...this.finishes.keys()].find((key) => this.remaining(key, index, count) > 0) || null;
    }

    unitPrice(key) {
      const finish = this.finishes.get(key);
      if (!finish || !finish.sources.length) return 0;
      const src =
        finish.sources.find((s) => (s.cap || 0) - (this.inCart.get(s.id) || 0) > 0) || finish.sources[0];
      return src.price || 0;
    }

    allocate() {
      const taken = new Map();
      for (let i = 0; i < this.state.count; i += 1) {
        const key = this.state.slots[i];
        const finish = this.finishes.get(key);
        const src =
          finish &&
          finish.sources.find(
            (s) => (s.cap || 0) - (this.inCart.get(s.id) || 0) - (taken.get(s.id) || 0) > 0
          );
        if (!src) return { lines: null, missing: finish ? finish.name : key };
        taken.set(src.id, (taken.get(src.id) || 0) + 1);
      }
      return { lines: [...taken].map(([id, quantity]) => ({ id, quantity })) };
    }

    async refreshCart() {
      try {
        const base = (window.routes && window.routes.cart_url) || '/cart';
        const res = await fetch(`${base}.js`, { headers: { Accept: 'application/json' } });
        if (!res.ok) return;
        const cart = await res.json();
        const next = new Map();
        (cart.items || []).forEach((item) => {
          next.set(item.variant_id, (next.get(item.variant_id) || 0) + item.quantity);
        });
        this.inCart = next;
      } catch (error) {
        // without the cart the page assumes it is empty; the add itself still answers for stock
      }
    }

    /* ---------- prices ---------- */

    totals(count = this.state.count) {
      let sum = 0;
      let atUnit = true;
      for (let i = 0; i < count; i += 1) {
        const unit = this.unitPrice(this.state.slots[i]);
        sum += unit;
        if (unit !== this.data.pricing.unit) atUnit = false;
      }
      let total = sum;
      const set = this.data.pricing.sets && this.data.pricing.sets[String(count)];
      if (this.data.pricing.live && count > 1 && atUnit && set > 0 && set < sum) total = set;
      return { sum, total };
    }

    /* ---------- drawing ---------- */

    render({ animate = true, previousCount = this.state.count, changed = -1 } = {}) {
      const motion = animate && !reducedMotion();
      const first = motion ? this.snapshot() : null;
      const count = this.state.count;
      const ghosts = motion && previousCount > count ? this.ghost(previousCount, count) : [];

      this.dataset.count = count;
      this.stage.dataset.count = count;

      this.objs.forEach((obj, i) => {
        const visible = i < count;
        const key = this.state.slots[i];
        const finish = this.finishes.get(key);
        const entering = motion && visible && i >= previousCount;
        obj.hidden = !visible;
        obj.toggleAttribute('data-last', i === count - 1);
        obj.dataset.finish = key;
        obj.classList.toggle('is-photo', !!(finish && finish.photo));
        const name = obj.querySelector('[data-cfg-obj-name]');
        if (name) name.textContent = finish ? finish.name.toUpperCase() : '';
        const n = obj.querySelector('.nt-cfg__tag-text b');
        if (n) n.textContent = pad(i + 1);
        obj.querySelectorAll('[data-cfg-move]').forEach((button) => {
          const dir = parseInt(button.dataset.cfgMove, 10);
          const target = i + dir;
          button.disabled = target < 0 || target >= count;
          button.setAttribute('aria-label', `Move object ${pad(i + 1)} ${dir < 0 ? 'left' : 'right'}`);
        });
        if (finish) this.setImage(obj, finish, motion && i === changed);
        if (entering) obj.classList.add('is-entering');
      });

      this.renderControls();
      this.renderPrice();
      this.renderCaption();
      this.writeUrl();
      this.announce();

      if (motion) {
        this.play(first);
        ghosts.forEach((ghost) => this.fadeGhost(ghost));
        requestAnimationFrame(() =>
          requestAnimationFrame(() => this.objs.forEach((obj) => obj.classList.remove('is-entering')))
        );
      }
    }

    setImage(obj, finish, fade) {
      const frame = obj.querySelector('.nt-cfg__frame');
      if (!frame) return;
      const current = frame.querySelector('.nt-cfg__img:not(.is-leaving)');
      if (!current) return;
      const want = finish.stage;
      if ((current.dataset.src || current.getAttribute('src')) === want) {
        current.dataset.src = want;
        return;
      }
      const srcset = `${finish.stageSmall} 360w, ${finish.stage} 720w`;
      if (!fade) {
        current.srcset = srcset;
        current.src = want;
        current.dataset.src = want;
        return;
      }
      const next = current.cloneNode(false);
      next.classList.add('is-incoming');
      next.removeAttribute('fetchpriority');
      next.removeAttribute('loading');
      next.srcset = srcset;
      next.src = want;
      next.dataset.src = want;
      frame.appendChild(next);
      const swap = () => {
        requestAnimationFrame(() => {
          current.classList.add('is-leaving');
          next.classList.remove('is-incoming');
          setTimeout(() => current.remove(), FADE_MS + 60);
        });
      };
      (next.decode ? next.decode() : Promise.resolve()).then(swap, swap);
    }

    renderControls() {
      const count = this.state.count;
      this.querySelectorAll('[data-cfg-count] input').forEach((input) => {
        const n = parseInt(input.value, 10);
        input.checked = n === count;
        const price = this.querySelector(`[data-cfg-count-price="${n}"]`);
        // the price that count would cost with the finishes already chosen for those positions
        if (price) this.writeMoney(price, this.totals(n).total);
      });

      this.slotEls.forEach((slot, i) => {
        slot.hidden = i >= count;
        const selected = this.state.slots[i];
        slot.querySelectorAll('[data-cfg-sw]').forEach((label) => {
          const key = label.dataset.cfgSw;
          const input = label.querySelector('input');
          const soldOut = this.capacity(key) <= 0;
          const usedUp = !soldOut && key !== selected && this.remaining(key, i) <= 0;
          input.disabled = soldOut || usedUp;
          input.checked = key === selected;
          label.classList.toggle('is-out', soldOut);
          label.classList.toggle('is-limited', usedUp);
        });
        this.renderSlotName(i);
      });

      const short = this.shortfall();
      this.querySelectorAll('[data-cfg-slot-msg]').forEach((msg, i) => {
        const text = short && short.index === i ? this.only(short.name) : '';
        msg.textContent = text;
        msg.hidden = !text;
      });
      const busy = this.addButton.getAttribute('aria-busy') === 'true';
      [this.addButton, this.barAdd].forEach((button) => {
        if (button && !busy) button.disabled = !!short;
      });
      if (this.addLabel && !busy && !this.addButton.classList.contains('is-added')) {
        this.addLabel.textContent = short ? this.labels.soldOut || 'Sold out' : this.labels.add || 'Add to bag';
      }
    }

    // The first position whose finish has run out once the earlier positions are served (a cart
    // update in another tab or the drawer can take the last one after the page loaded).
    shortfall() {
      const used = new Map();
      for (let i = 0; i < this.state.count; i += 1) {
        const key = this.state.slots[i];
        used.set(key, (used.get(key) || 0) + 1);
        if (used.get(key) > this.capacity(key)) {
          const finish = this.finishes.get(key);
          return { index: i, name: finish ? finish.name : key };
        }
      }
      return null;
    }

    only(name) {
      return (this.labels.onlyLeft || 'No more [finish] in stock for this slot.').replace('[finish]', name);
    }

    renderSlotName(index, previewKey, previewLabel) {
      const slot = this.slotEls[index];
      if (!slot) return;
      const key = previewKey || this.state.slots[index];
      const finish = this.finishes.get(key);
      const nameEl = slot.querySelector('[data-cfg-slot-finish]');
      const noteEl = slot.querySelector('[data-cfg-slot-note]');
      const row = slot.querySelector('[data-cfg-slot-name]');
      let name = finish ? finish.name.toUpperCase() : '';
      if (previewKey && previewLabel) {
        if (previewLabel.classList.contains('is-out')) {
          name += ` · ${(this.labels.soldOut || 'Sold out').toUpperCase()}`;
        } else if (previewLabel.classList.contains('is-limited')) {
          name += ' · NONE LEFT';
        }
      }
      if (nameEl && nameEl.textContent !== name) nameEl.textContent = name;
      if (noteEl) noteEl.textContent = finish && finish.note ? finish.note.trim() : '';
      if (row) row.classList.toggle('is-preview', !!previewKey && previewKey !== this.state.slots[index]);
    }

    renderPrice() {
      const count = this.state.count;
      const { sum, total } = this.totals();
      const objects = `${pad(count)} ${count === 1 ? this.labels.object || 'object' : this.labels.objects || 'objects'}`;
      this.writeMoney(this.querySelector('[data-cfg-total]'), total);
      this.writeMoney(this.querySelector('[data-cfg-bar-total]'), total);
      this.writeMoney(this.querySelector('[data-cfg-sum]'), sum);
      const ref = this.querySelector('[data-cfg-ref]');
      if (ref) ref.hidden = !(total < sum);
      this.setText('[data-cfg-objects-label]', objects);
      this.setText('[data-cfg-bar-label]', objects);
      this.querySelectorAll('[data-cfg-bar-obj]').forEach((img, i) => {
        img.hidden = i >= count;
        const finish = this.finishes.get(this.state.slots[i]);
        if (finish && img.getAttribute('src') !== finish.stageSmall) img.src = finish.stageSmall;
      });
    }

    // Writes a price only when it changed. A currency app converts the .money span in place, so
    // the amount last written is kept on the element and compared, never the converted text.
    writeMoney(el, cents) {
      if (!el) return;
      const key = String(cents);
      if (el.dataset.cents === key) return;
      el.innerHTML = formatMoney(cents);
      el.dataset.cents = key;
    }

    renderCaption() {
      const names = this.state.slots.slice(0, this.state.count).map((key) => {
        const finish = this.finishes.get(key);
        return finish ? finish.name : key;
      });
      this.setText('[data-cfg-code]', names.map((n) => n.toUpperCase()).join(' / '));
      this.setText('[data-cfg-alt]', `${this.data.productTitle}: ${names.join(', ')}`);
    }

    setText(selector, text) {
      const el = this.querySelector(selector);
      if (el && el.textContent !== text) el.textContent = text;
    }

    // Reads the composition out after a change. The first draw is the page itself, and a redraw
    // that changes nothing (the cart refresh after load, the redraw after adding) stays quiet, so
    // "added to your bag" is not talked over.
    announce() {
      const key = JSON.stringify([this.state.count, this.state.slots.slice(0, this.state.count), this.totals().total]);
      const first = this.lastAnnounced === undefined;
      if (key === this.lastAnnounced) return;
      this.lastAnnounced = key;
      if (first) return;
      clearTimeout(this.timers.get('announce'));
      this.timers.set(
        'announce',
        setTimeout(() => {
          const { total } = this.totals();
          const count = this.state.count;
          const names = this.state.slots
            .slice(0, count)
            .map((key) => (this.finishes.get(key) || {}).name || key);
          const objects = count === 1 ? this.labels.object || 'object' : this.labels.objects || 'objects';
          this.say(`${count} ${objects}: ${names.join(', ')}. ${textOf(formatMoney(total))}.`);
        }, 450)
      );
    }

    say(text) {
      if (!this.statusEl || !text) return;
      this.statusEl.textContent = '';
      requestAnimationFrame(() => {
        this.statusEl.textContent = text;
      });
    }

    /* ---------- motion ---------- */

    snapshot() {
      const rects = new Map();
      this.objs.forEach((obj) => {
        if (!obj.hidden) rects.set(obj, obj.getBoundingClientRect());
      });
      return rects;
    }

    play(first) {
      if (!first) return;
      this.objs.forEach((obj) => {
        const was = first.get(obj);
        if (!was || obj.hidden) return;
        const now = obj.getBoundingClientRect();
        if (!now.width || !now.height) return;
        const dx = was.left - now.left;
        const dy = was.bottom - now.bottom;
        const scale = was.width / now.width;
        if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5 && Math.abs(scale - 1) < 0.005) return;
        obj.style.transition = 'none';
        obj.style.transformOrigin = '0 100%';
        obj.style.transform = `translate(${dx}px, ${dy}px) scale(${scale})`;
        obj.getBoundingClientRect();
        obj.style.transition = `transform ${MOVE_MS}ms cubic-bezier(0.2, 0.7, 0.2, 1)`;
        obj.style.transform = '';
        const done = () => {
          obj.style.transition = '';
          obj.style.transformOrigin = '';
          obj.removeEventListener('transitionend', done);
        };
        obj.addEventListener('transitionend', done);
        setTimeout(done, MOVE_MS + 80);
      });
    }

    // Objects leaving the composition fade out where they stood, while the rest regroup.
    ghost(previous, next) {
      const stageRect = this.stage.getBoundingClientRect();
      const ghosts = [];
      for (let i = next; i < previous; i += 1) {
        const obj = this.objs[i];
        if (!obj || obj.hidden) continue;
        const rect = obj.getBoundingClientRect();
        const clone = obj.cloneNode(true);
        clone.removeAttribute('data-cfg-obj');
        clone.removeAttribute('data-last');
        clone.querySelectorAll('button, .nt-cfg__measure').forEach((el) => el.remove());
        clone.classList.add('nt-cfg__ghost');
        clone.setAttribute('aria-hidden', 'true');
        clone.style.left = `${rect.left - stageRect.left}px`;
        clone.style.top = `${rect.top - stageRect.top}px`;
        clone.style.width = `${rect.width}px`;
        clone.style.height = `${rect.height}px`;
        this.stage.appendChild(clone);
        ghosts.push(clone);
      }
      return ghosts;
    }

    fadeGhost(ghost) {
      requestAnimationFrame(() => {
        ghost.classList.add('is-gone');
        setTimeout(() => ghost.remove(), FADE_MS + 80);
      });
    }

    preload() {
      const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 1200));
      idle(() => {
        const wide = window.matchMedia('(min-width: 990px)').matches;
        this.finishes.forEach((finish) => {
          if (!finish.stage) return;
          const img = new Image();
          img.sizes = wide ? '22vw' : '32vw';
          img.srcset = `${finish.stageSmall} 360w, ${finish.stage} 720w`;
          img.src = finish.stage;
        });
      });
    }

    /* ---------- page address ---------- */

    applyUrlState() {
      if (!this.data.shareUrl) return;
      let params;
      try {
        params = new URL(window.location.href).searchParams;
      } catch (error) {
        return;
      }
      const set = parseInt(params.get('set'), 10);
      const keys = (params.get('f') || '').split('.').filter(Boolean);
      if (!set && !keys.length) return;
      if (set >= 1 && set <= MAX) this.state.count = set;
      keys.slice(0, MAX).forEach((key, i) => {
        if (this.finishes.has(key)) this.state.slots[i] = key;
      });
      for (let i = 0; i < this.state.count; i += 1) {
        if (this.remaining(this.state.slots[i], i) <= 0) {
          const alt = this.firstAvailable(i, this.state.count);
          if (alt) this.state.slots[i] = alt;
        }
      }
    }

    writeUrl() {
      if (!this.data.shareUrl || !window.history || !window.history.replaceState) return;
      const snapshot = JSON.stringify([this.state.count, this.state.slots.slice(0, this.state.count)]);
      // Leave the landing address (ad parameters, ?variant=) alone until the visitor changes something.
      if (this.landing === undefined) {
        this.landing = snapshot;
        return;
      }
      if (snapshot === this.landing) return;
      this.landing = null;
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete('variant');
        url.searchParams.set('set', String(this.state.count));
        url.searchParams.set('f', this.state.slots.slice(0, this.state.count).join('.'));
        window.history.replaceState(window.history.state, '', url.toString());
      } catch (error) {
        // the address is a convenience; the page works without it
      }
    }

    /* ---------- mobile bar ---------- */

    // On phones the stage sits above the controls, so choosing the second and third finish scrolls
    // it away. The bar keeps the composition in view (in miniature, with the price and the button)
    // whenever neither the stage nor the main button is on screen. Like the house bar
    // (assets/notice-sticky-atc.js) it steps aside for the footer, and while it shows the body
    // carries nt-cfg-bar-on, which hides the currency selector and pads the page end.
    watchBar() {
      if (!this.bar || !this.addButton || !this.stage || !('IntersectionObserver' in window)) return;
      const narrow = window.matchMedia('(max-width: 989px)');
      const footer = document.querySelector('footer.footer') || document.querySelector('footer');
      const seen = new Map();
      const share = (target) => (target === this.stage ? 0.34 : target === footer ? 0.4 : 0);
      const update = () => {
        if (!seen.has(this.stage) || !seen.has(this.addButton)) return;
        const on = narrow.matches && !seen.get(this.stage) && !seen.get(this.addButton) && !seen.get(footer);
        this.bar.hidden = !on;
        document.body.classList.toggle('nt-cfg-bar-on', on);
      };
      this.barObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            seen.set(entry.target, entry.isIntersecting && entry.intersectionRatio >= share(entry.target));
          });
          update();
        },
        { threshold: [0, 0.34, 0.4] }
      );
      this.barObserver.observe(this.stage);
      this.barObserver.observe(this.addButton);
      if (footer) this.barObserver.observe(footer);
      if (narrow.addEventListener) narrow.addEventListener('change', update);
    }

    measureHeader() {
      const header = document.querySelector('sticky-header') || document.querySelector('.section-header');
      if (!header) return;
      const set = () => {
        const h = Math.round(header.getBoundingClientRect().height);
        if (h > 0 && h < 200) this.style.setProperty('--nt-cfg-top', `${h}px`);
      };
      set();
      if ('ResizeObserver' in window) new ResizeObserver(set).observe(header);
    }

    /* ---------- adding ---------- */

    cart() {
      return document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    }

    sectionsToRender(cart) {
      if (cart && typeof cart.getSectionsToRender === 'function') return cart.getSectionsToRender();
      return [{ id: 'cart-icon-bubble' }];
    }

    genericError() {
      return (window.cartStrings && window.cartStrings.error) || 'Could not add to the bag. Try again.';
    }

    async add(button) {
      if (this.busy || this.addButton.disabled) return;
      this.hideError();
      const { lines, missing } = this.allocate();
      if (!lines) {
        this.showError(this.only(missing));
        this.render({ animate: false });
        return;
      }

      this.busy = true;
      this.resetAdded();
      [this.addButton, this.barAdd].forEach((b) => {
        if (!b) return;
        b.setAttribute('aria-busy', 'true');
        b.setAttribute('aria-disabled', 'true');
      });
      if (this.addLabel) this.addLabel.textContent = this.labels.adding || 'Adding';

      const started = performance.now();
      let response = null;
      let failure = null;
      try {
        try {
          response = await this.post(lines);
        } catch (error) {
          failure = error;
        }
        if (response) {
          // Tracking first, as product-form.js does: the lines are in the bag whatever the drawer does.
          this.publishAdds(response);
          try {
            this.renderCart(response, button);
          } catch (error) {
            console.error(error);
          }
        }
        const remaining = MIN_BUSY_MS - (performance.now() - started);
        if (remaining > 0) await wait(remaining);
      } finally {
        // whatever happened above, the button must not stay locked
        [this.addButton, this.barAdd].forEach((b) => {
          if (!b) return;
          b.removeAttribute('aria-busy');
          b.removeAttribute('aria-disabled');
        });
        this.busy = false;
      }

      await this.refreshCart();
      if (response) this.markAdded();
      this.render({ animate: false });
      if (response) {
        const count = this.state.count;
        const objects = count === 1 ? this.labels.object || 'object' : this.labels.objects || 'objects';
        this.say(`${count} ${objects} added to your bag.`);
      } else {
        const message = (failure && failure.message) || this.genericError();
        this.showError(message);
        this.say(message);
      }
    }

    async post(lines) {
      const sections = this.sectionsToRender(this.cart());
      const init = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          items: lines,
          sections: sections.map((section) => section.id),
          sections_url: window.location.pathname,
        }),
      };
      if (typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function') {
        init.signal = AbortSignal.timeout(10000);
      }
      let res;
      try {
        res = await fetch((window.routes && window.routes.cart_add_url) || '/cart/add', init);
      } catch (error) {
        throw new Error(
          error && error.name === 'TimeoutError' ? 'The bag took too long to answer. Try again.' : this.genericError()
        );
      }
      let data = null;
      try {
        data = await res.json();
      } catch (error) {
        data = null;
      }
      if (!res.ok || !data || data.status) {
        throw new Error((data && (data.description || data.message)) || this.genericError());
      }
      return data;
    }

    renderCart(response, button) {
      const cart = this.cart();
      if (cart && typeof cart.renderContents === 'function') {
        // focus returns to this button when the drawer closes
        if (button && typeof cart.setActiveElement === 'function') cart.setActiveElement(button);
        const first = Array.isArray(response.items) ? response.items[0] : null;
        cart.renderContents(
          cart.tagName === 'CART-NOTIFICATION' && first && !response.key
            ? Object.assign({}, response, { key: first.key })
            : response
        );
        // A page that loaded with an empty bag carries is-empty on the drawer, which hides the
        // lines; product-form.js clears it after an add the same way (so does notice-addons.js).
        cart.classList.remove('is-empty');
        const inner = cart.querySelector('.drawer__inner');
        if (inner) inner.classList.remove('is-empty');
        return;
      }
      window.location.href = (window.routes && window.routes.cart_url) || '/cart';
    }

    publishAdds(response) {
      const events = pubSubEvents();
      if (!events || typeof publish !== 'function') return;
      const lines = Array.isArray(response.items) ? response.items : [response];
      lines.forEach((line) => {
        try {
          publish(events.cartUpdate, {
            source: 'product-form',
            origin: 'notice-spring-configurator',
            productVariantId: String(line.variant_id || line.id),
            cartData: line,
          });
        } catch (error) {
          // a listener that throws must not cost the next line its events
          console.error(error);
        }
      });
    }

    markAdded() {
      this.addButton.classList.add('is-added');
      if (this.addLabel) this.addLabel.textContent = this.labels.added || 'Added to bag';
      clearTimeout(this.timers.get('added'));
      this.timers.set(
        'added',
        setTimeout(() => {
          this.addButton.classList.remove('is-added');
          this.renderControls();
        }, ADDED_MS)
      );
    }

    resetAdded() {
      clearTimeout(this.timers.get('added'));
      this.addButton.classList.remove('is-added');
    }

    showError(message) {
      if (!this.errorEl || !message) return;
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
      clearTimeout(this.timers.get('error'));
      this.timers.set('error', setTimeout(() => this.hideError(), ERROR_MS));
    }

    hideError() {
      if (!this.errorEl) return;
      clearTimeout(this.timers.get('error'));
      this.errorEl.hidden = true;
      this.errorEl.textContent = '';
    }
  }

  customElements.define('notice-spring-configurator', NoticeSpringConfigurator);
})();
